import { StoredOverspeedIncident, useReservationOverspeedHistory } from '../api/overspeed'
import { Button } from '../components/button'

function formatDateTime(iso: string): string {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return iso
	return date.toLocaleString('cs-CZ', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function formatDuration(seconds: number): string {
	const s = Math.max(0, Math.round(seconds))
	if (s < 60) return `${s} s`
	const m = Math.floor(s / 60)
	const rest = s % 60
	if (m < 60) return rest ? `${m} min ${rest} s` : `${m} min`
	const h = Math.floor(m / 60)
	return `${h} h ${m % 60} min`
}

function escapeHtml(value: string): string {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function buildPrintHtml(reservationId: string, rows: Array<StoredOverspeedIncident>): string {
	const body = rows
		.map(
			r => `<tr>
				<td>${escapeHtml(formatDateTime(r.started_at))}</td>
				<td>${escapeHtml(formatDateTime(r.ended_at))}</td>
				<td>${escapeHtml(formatDuration(r.duration_seconds))}</td>
				<td class="speed">${r.max_speed} km/h</td>
				<td>${r.speed_limit != null ? `${r.speed_limit} km/h` : '—'}</td>
				<td>${r.lat != null && r.lng != null ? `${r.lat}, ${r.lng}` : '—'}</td>
			</tr>`,
		)
		.join('')

	return `<!doctype html><html lang="cs"><head><meta charset="utf-8">
		<title>Historie překročení rychlosti – rezervace ${escapeHtml(reservationId)}</title>
		<style>
			body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 32px; }
			h1 { font-size: 18px; margin: 0 0 4px; }
			.sub { color: #555; font-size: 12px; margin: 0 0 20px; }
			table { border-collapse: collapse; width: 100%; font-size: 13px; }
			th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
			thead th { background: #f3f4f6; }
			.speed { font-weight: bold; color: #b91c1c; }
			@media print { body { margin: 12mm; } }
		</style></head>
		<body>
			<h1>Historie překročení rychlosti</h1>
			<p class="sub">Rezervace ID: ${escapeHtml(reservationId)} · ${rows.length} překročení · vytištěno ${escapeHtml(
				new Date().toLocaleString('cs-CZ'),
			)}</p>
			<table>
				<thead><tr><th>Od</th><th>Do</th><th>Trvání</th><th>Max. rychlost</th><th>Limit</th><th>Místo (lat, lng)</th></tr></thead>
				<tbody>${body}</tbody>
			</table>
			<script>window.onload = function () { window.print() }</script>
		</body></html>`
}

function printHistory(reservationId: string, rows: Array<StoredOverspeedIncident>) {
	const win = window.open('', '_blank', 'width=900,height=700')
	if (!win) return
	win.document.write(buildPrintHtml(reservationId, rows))
	win.document.close()
	win.focus()
}

export function OverspeedHistory({ reservationId }: { reservationId: string }) {
	const { data = [], isLoading } = useReservationOverspeedHistory(reservationId)

	if (isLoading) return null

	return (
		<div className="mt-6 rounded-xl border border-gray-200 bg-white">
			<div className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3">
				<h3 className="text-md font-bold">
					Historie překročení rychlosti{' '}
					{data.length ? (
						<span className="font-normal text-gray-500">({data.length})</span>
					) : null}
				</h3>
				{data.length ? (
					<div className="flex gap-2">
						<Button
							variant="SECONDARY"
							size="SMALL"
							onPress={() => printHistory(reservationId, data)}
						>
							Tisk
						</Button>
						<Button
							variant="SECONDARY"
							size="SMALL"
							onPress={() => printHistory(reservationId, data)}
						>
							Uložit do PDF
						</Button>
					</div>
				) : null}
			</div>

			<div className="p-4">
				{data.length === 0 ? (
					<p className="text-sm text-gray-500">
						Zatím žádné uložené překročení rychlosti. Historie se plní průběžně (denní
						kontrola) a při ruční kontrole v sekci výše.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-left text-sm">
							<thead className="text-gray-500">
								<tr>
									<th className="py-1 pr-3 font-medium">Od</th>
									<th className="py-1 pr-3 font-medium">Do</th>
									<th className="py-1 pr-3 font-medium">Trvání</th>
									<th className="py-1 pr-3 font-medium">Max. rychlost</th>
									<th className="py-1 pr-3 font-medium">Limit</th>
									<th className="py-1 pr-3 font-medium">Místo</th>
								</tr>
							</thead>
							<tbody>
								{data.map((row, index) => (
									<tr key={index} className="border-t border-gray-100">
										<td className="py-1.5 pr-3">
											{formatDateTime(row.started_at)}
										</td>
										<td className="py-1.5 pr-3">
											{formatDateTime(row.ended_at)}
										</td>
										<td className="py-1.5 pr-3">
											{formatDuration(row.duration_seconds)}
										</td>
										<td className="py-1.5 pr-3 font-semibold text-rose-700">
											{row.max_speed} km/h
										</td>
										<td className="py-1.5 pr-3">
											{row.speed_limit != null
												? `${row.speed_limit} km/h`
												: '—'}
										</td>
										<td className="py-1.5 pr-3">
											{row.lat != null && row.lng != null ? (
												<a
													className="text-blue-600 hover:underline"
													href={`https://www.google.com/maps?q=${row.lat},${row.lng}`}
													target="_blank"
													rel="noreferrer"
												>
													mapa
												</a>
											) : (
												'—'
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	)
}
